import { DialogContent } from "@/view/components/ui/dialog";
import { Spinner } from "@/view/components/ui/spinner";
import { PartnerType } from "@/app/utils/partners.enum";
import { ChevronRight, LinkIcon } from "lucide-react";
import { ProductCatalogItem } from "@/app/services/configService/getProductsCatalog";
import { useState } from "react";
import ButtonBack from "@/view/components/ButtonBack";

interface ChooseSimulationModalProps {
  handleOpenLinkModal: () => void;
  onOptionSelect: (option: PartnerType) => void;
  isLoading: boolean;
  catalog?: ProductCatalogItem[];
}

export default function ChooseSimulationModal({
  handleOpenLinkModal,
  onOptionSelect,
  isLoading,
  catalog,
}: ChooseSimulationModalProps) {
  const availableItems = (catalog ?? []).filter((item) => item.available);

  const cltKeys = [PartnerType.PAN, PartnerType.CLT, PartnerType.C6];

  const cltItems = availableItems.filter((item) =>
    cltKeys.includes(item.key as PartnerType),
  );

  const regularItems = availableItems.filter(
    (item) => !cltKeys.includes(item.key as PartnerType),
  );

  const [step, setStep] = useState<"products" | "clt">("products");

  if (isLoading) {
    return (
      <DialogContent className="w-full max-w-3xl" showSupportButton={false}>
        <div className="flex items-center justify-center py-8">
          <Spinner />
        </div>
      </DialogContent>
    );
  }
  if (!catalog || availableItems.length === 0) {
    return (
      <DialogContent className="w-full max-w-3xl" showSupportButton={false}>
        <div className="flex items-center justify-center py-8">
          <p>Nenhum produto encontrado</p>
        </div>
      </DialogContent>
    );
  }

  return (
    <DialogContent
      className="max-h-[95vh] w-full max-w-3xl overflow-y-auto p-6"
      showCloseButton={false}
      showSupportButton={false}
    >
      <div className="flex flex-col gap-3">
        {step === "products" && (
          <>
            {regularItems.map((item) => (
              <Card
                key={item.key}
                option={item.key}
                name={item.label}
                description={item.description}
                image={item.image}
                color={item.color}
                onOptionSelect={(option) => option && onOptionSelect(option)}
              />
            ))}

            {cltItems.length === 1 && (
              <Card
                key={cltItems[0].key}
                option={cltItems[0].key}
                name={cltItems[0].label}
                description={cltItems[0].description}
                image={cltItems[0].image}
                color={cltItems[0].color}
                onOptionSelect={(option) => option && onOptionSelect(option)}
              />
            )}

            {cltItems.length >= 2 && (
              <Card
                option="link"
                name="CLT"
                description="Consignado privado"
                onOptionSelect={() => setStep("clt")}
                image="/clt_product.png"
                imageContainerClassName="bg-primary"
              />
            )}

            <Card
              option="link"
              name="Link"
              description="Criar link de autocontratação"
              onOptionSelect={() => handleOpenLinkModal()}
              icon={<LinkIcon />}
            />
          </>
        )}

        {step === "clt" && (
          <>
            <ButtonBack
              label="CLT"
              action={() => setStep("products")}
              className="mb-2"
            />

            {cltItems.map((item) => (
              <Card
                key={item.key}
                option={item.key}
                name={item.label}
                description={item.description}
                image={item.image}
                color={item.color}
                onOptionSelect={(option) => option && onOptionSelect(option)}
              />
            ))}
          </>
        )}
      </div>
    </DialogContent>
  );
}

const Card = ({
  option,
  name,
  description,
  onOptionSelect,
  image,
  color,
  icon: Icon,
  imageContainerClassName,
}: {
  option: PartnerType | "link";
  name: string;
  description: string;
  onOptionSelect: (option?: PartnerType) => void;
  image?: string;
  color?: string;
  icon?: React.ReactNode;
  imageContainerClassName?: string;
}) => {
  return (
    <div
      key={option}
      onClick={() => onOptionSelect(option === "link" ? undefined : option)}
      className="flex cursor-pointer items-center gap-4 rounded-lg border border-gray01 p-3 transition-all hover:border-primary hover:shadow-sm"
    >
      <div
        className={`flex h-14 w-24 shrink-0 items-center justify-center rounded-md ${imageContainerClassName ?? ""}`}
        style={
          !imageContainerClassName
            ? { backgroundColor: color ?? "gray" }
            : undefined
        }
      >
        {image ? (
          <img src={image} alt={option} className={`h-10 w-auto scale-90`} />
        ) : (
          <div>
            <span className="text-base font-semibold text-white">
              {Icon || option}
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-col">
        <span className="text-base font-semibold text-black01">{name}</span>
        <span className="text-sm text-black03">{description}</span>
      </div>
      <ChevronRight className="ml-auto h-5 w-5 text-black04" />
    </div>
  );
};
